import {
  convertViemChainToRelayChain,
  createClient,
  MAINNET_RELAY_API,
  TESTNET_RELAY_API,
} from '@reservoir0x/relay-sdk';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider } from 'posthog-js/react';
import { useEffect } from 'react';
import { base, baseSepolia, mainnet, optimism, sepolia, shape } from 'viem/chains';
import { Network } from '@manifoldxyz/js-ts-utils';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import 'react-loading-skeleton/dist/skeleton.css';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports, import/no-extraneous-dependencies
import 'tippy.js/dist/tippy.css';
import { appsApi } from '@/api/apps';
import { ClaimStudioInstance, ScribeStudioInstance } from '@/api/models/studioInstance';
import { IAnalytics } from '@/common/telemetry';
import { useWidgetNameProps as useClaimWidgetNameProps } from '@/exports/ClaimCompleteWidget';
import { useWidgetNameProps as useScribeWidgetNameProps } from '@/exports/ScribeCompleteWidget';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useInitializeCrossMintOnce } from '@/hooks/useInitializeCrossMintOnce';
import { useInitializeProviderOnce } from '@/hooks/useInitializeProviderOnce';
import useMainStore from '@/store/mainStore';
import ClaimLayout from './ClaimLayout';
import ScribeLayout from './ScribeLayout';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * @important
 * wrapper for the PostHogProvider to only initialize it on production
 */
const PostHogProviderWrapper: React.FC<{
  children: React.ReactElement;
  analytics?: IAnalytics;
}> = ({ children, analytics }) => {
  if (analytics && process.env.PUBLIC_POSTHOG_KEY && process.env.PUBLIC_POSTHOG_HOST) {
    return (
      <PostHogProvider
        apiKey={process.env.PUBLIC_POSTHOG_KEY}
        options={{
          api_host: process.env.PUBLIC_POSTHOG_HOST,
          person_profiles: 'identified_only',
          autocapture: false,
          rageclick: false,
          capture_pageview: true, // pageview is captured automatically
          capture_pageleave: true,
          disable_session_recording: true,
        }}
      >
        {children}
      </PostHogProvider>
    );
  }
  return <>{children}</>;
};

export default function Container() {
  // main store
  const setStudioInstance = useMainStore((state) => state.setStudioInstance);

  // hooks
  const { id: claimId } = useClaimWidgetNameProps();
  const { id: scribeId } = useScribeWidgetNameProps();
  const analytics = useAnalytics();

  // intializations
  useInitializeCrossMintOnce();
  useInitializeProviderOnce();

  // Fetch instance data immediately
  useEffect(() => {
    async function handler() {
      let instance: ClaimStudioInstance | ScribeStudioInstance;
      if (claimId) {
        instance = await appsApi.getClaimInstance(claimId);
        setStudioInstance(instance);
      } else if (scribeId) {
        instance = await appsApi.getScribeInstance(scribeId);
        setStudioInstance(instance);
      } else {
        throw new Error('No instance ID provided');
      }

      // initialize relay client
      createClient({
        baseApiUrl: Network.isTestnet(instance.publicData.network)
          ? TESTNET_RELAY_API
          : MAINNET_RELAY_API,
        source: 'app.manifold.xyz',
        chains: [
          convertViemChainToRelayChain(mainnet),
          convertViemChainToRelayChain(base),
          convertViemChainToRelayChain(optimism),
          convertViemChainToRelayChain(shape),
          convertViemChainToRelayChain(sepolia),
          convertViemChainToRelayChain(baseSepolia),
        ],
      });
    }
    handler();
  }, [claimId, scribeId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PostHogProviderWrapper analytics={analytics}>
      <QueryClientProvider client={queryClient}>
        {claimId ? <ClaimLayout /> : <ScribeLayout />}
      </QueryClientProvider>
    </PostHogProviderWrapper>
  );
}
